import {
  createGuid,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  CallbackProperty,
  Cartesian3,
  defined,
  PolygonHierarchy,
  Cartographic,
  SceneTransforms,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";
import MeasurementVolume from "../measurement/MeasurementVolume";
import DitapAlert from "../popup/DitapAlert";
import PopupMeasurementVolume from "../popup/PopupMeasurementVolume";
import Utils from "../basic/Utils";

/**
 * 체적량 측정을 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input (mouse/keyboard) class for Volumetric Measurement.
 * @alias InteractionVolume
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스. <br> The DitapViewer instance to use.
 *
 * @param {object} volumeOption 각각 다음과 같은 속성을 가진 객체. <br> Object with the following properties:
 * @param {MeasurementVolume} [volumeOption.measurementVolume=undefined] 체적량 측정 연산 및 영역 표시 클래스. <br> Volumetric Measurement Calculation and Region Display Class.
 * @param {Function} [volumeOption.startCallback=undefined] 시작점 생성 콜백 함수. <br> Startpoint generation callback function.
 * @param {Function} [volumeOption.addCallback=undefined] 포인트 추가 콜백 함수. <br> Point add callback function.
 * @param {Function} [volumeOption.endCallback=undefined] 종료점 생성 콜백 함수. <br> Endpoint generation callback function.
 *
 * @example
 * const getUrlFromBase64 = (base64Data) => {
 *   const b64Data = base64Data;
 *   const byteCharacters = window.atob(b64Data);
 *   const byteNumbers = new Array(byteCharacters.length);
 *   for (let i = 0; i < byteCharacters.length; i++) {
 *     byteNumbers[i] = byteCharacters.charCodeAt(i);
 *   }
 *   const byteArray = new Uint8Array(byteNumbers);
 *   const blob = new Blob([byteArray], { type: "image/png" });
 *
 *   return URL.createObjectURL(blob);
 * };
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   baseLayerPicker: true,
 *   // 지형(터레인 지정)
 *   terrainProviderViewModels: [
 *     new Ditap.ProviderViewModel({
 *       name: "Ditap Korea Terrain",
 *       iconUrl: getUrlFromBase64(
 *         Ditap.ConstantUrl.DITAP_TERRAIN_IMG_URL
 *       ),
 *       tooltip: "WGS84 standard ellipsoid, also known as EPSG:4326",
 *       category: "Ditap",
 *       creationFunction: function () {
 *         return Ditap.NationwideTerrainProvider.create();
 *       },
 *     }),
 *   ],
 * });
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * // 1. 생성 시 MeasurementVolume 할당
 * const measurementVolume = new Ditap.MeasurementVolume(viewer);
 * const props = {
 *   measurementVolume: measurementVolume
 * };
 * const interactionVolume = new Ditap.InteractionVolume(viewer, props);
 * interactionVolume.on();
 *
 * // 2. 생성 후 MeasurementVolume 할당
 * const measurementVolume = new Ditap.MeasurementVolume(viewer);
 * const interactionVolume = new Ditap.InteractionVolume(viewer);
 * interactionVolume.measurementVolume = measurementVolume;
 * interactionVolume.on();
 *
 */
class InteractionVolume {
  constructor(viewer, volumeOption) {
    this._viewer = undefined;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      this.depthTest = viewer.scene.globe.depthTestAgainstTerrain;
    } else {
      throw Error("InteractionVolume viewer type error");
    }

    this._measurementVolume = undefined;
    if (
      volumeOption &&
      volumeOption.measurementVolume instanceof MeasurementVolume
    ) {
      this._measurementVolume = volumeOption.measurementVolume;
    } else if (volumeOption?.measurementVolume === undefined) {
      this._measurementVolume = undefined;
    } else {
      throw Error("InteractionVolume measurementVolume type error");
    }

    this._infoLabelEntity = undefined; // 인포 라벨 엔티티
    this._drawingpositions = []; // 영역 지정 좌표 목록
    this._drawingPointEntity = []; // 영역 지정 포인트 목록
    this._drawingPolylineEntity = undefined; // 영역 지정 폴리라인
    this._drawingPolygonEntity = undefined; // 영역 지정 폴리곤
    this._drawingLabelEntity = []; // 영역 지정 라벨
    this._mousePosition = undefined; // 마우스 좌표
    this._handler = undefined; // 핸들러
    this._isOn = false; // 핸들러 실행 여부

    this._keyDownEvent = undefined;
    this._buttonElement = undefined;
    this._createWarningAlert = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    this._popup = true;

    if (volumeOption) {
      if (volumeOption.startCallback instanceof Function) {
        this._startCallback = volumeOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (volumeOption.addCallback instanceof Function) {
        this._addCallback = volumeOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (volumeOption.endCallback instanceof Function) {
        this._endCallback = volumeOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스. </br>
   * DitapViewer.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }

  /**
   * 체적량 측정 연산 및 영역 표시 클래스. </br>
   * MeasurementVolume instance.
   * @type {MeasurementVolume | undefined}
   */
  get measurementVolume() {
    return this._measurementVolume;
  }
  set measurementVolume(measurementVolume) {
    if (
      measurementVolume instanceof MeasurementVolume ||
      measurementVolume === undefined
    ) {
      this._measurementVolume = measurementVolume;
    } else {
      console.error("Type of parameter doesn't match");
    }
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }

  /*
   * 영역 지정 중인 포인트 엔티티. </br>
   * Volume draw point entity.
   * @type {Entity}
   */
  get drawingPointEntity() {
    return this._drawingPointEntity;
  }
  set drawingPointEntity(drawingPointEntity) {
    this._drawingPointEntity = drawingPointEntity;
  }

  /*
   * 영역 지정 중인 폴리라인 엔티티. </br>
   * Volume draw polyline entity.
   * @type {Entity}
   */
  get drawingPolylineEntity() {
    return this._drawingPolylineEntity;
  }
  set drawingPolylineEntity(drawingPolylineEntity) {
    this._drawingPolylineEntity = drawingPolylineEntity;
  }

  /*
   * 영역 지정 중인 폴리곤 엔티티. </br>
   * Volume draw polygon entity.
   * @type {Entity}
   */
  get drawingPolygonEntity() {
    return this._drawingPolygonEntity;
  }
  set drawingPolygonEntity(drawingPolygonEntity) {
    this._drawingPolygonEntity = drawingPolygonEntity;
  }

  /*
   * 영역 지정 도움말 라벨 엔티티. </br>
   * Volume draw label entity.
   * @type {Entity}
   */
  get drawingLabelEntity() {
    return this._drawingLabelEntity;
  }
  set drawingLabelEntity(drawingLabelEntity) {
    this._drawingLabelEntity = drawingLabelEntity;
  }

  /*
   * 영역 좌표. </br>
   * Volume draw position.
   * @type {Cartesian3[]}
   */
  get drawingPositions() {
    return this._drawingPositions;
  }
  set drawingPositions(drawingPositions) {
    this._drawingPositions = drawingPositions;
  }

  /*
   * 마우스 좌표. </br>
   * Mouse position.
   * @type {Cartesian3}
   */
  get mousePosition() {
    return this._mousePosition;
  }
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화 상태.</br>
   * Handler enabled or disabled.
   * @readonly
   * @type {boolean}
   */
  get isOn() {
    return this._isOn;
  }
  set isOn(isOn) {
    this._isOn = isOn;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert.
   * @type {Element}
   */
  get createWarningAlert() {
    return this._createWarningAlert;
  }
  set createWarningAlert(createWarningAlert) {
    this._createWarningAlert = createWarningAlert;
  }

  /*
   * 체적량 측정 후 기본 팝업 표시 여부. </br>
   * After volumetric measurement, display the default popup.
   * @type {boolean}
   */
  get popup() {
    return this._popup;
  }
  set popup(popup) {
    this._popup = popup;
  }

  /**
   * 측정 시작 콜백 함수. <br>
   * Measurement start callback function.
   * @type {Function | undefined}
   */
  set startCallback(startCallback) {
    this._startCallback = startCallback;
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 포인트 생성 콜백 함수. <br>
   * Measurement point add callback function.
   * @type {Function | undefined}
   */
  set addCallback(addCallback) {
    this._addCallback = addCallback;
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 포인트 생성 콜백 함수. <br>
   * Measurement end callback function.
   * @type {Function | undefined}
   */
  set endCallback(endCallback) {
    this._endCallback = endCallback;
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 체적 측정 핸들러 실행. (MeasurementVolume 초기화 필수.) </br>
   * Execute volume measurement handler. (MeasurementVolume initialization is required.)
   */
  on() {
    if (!(this.measurementVolume instanceof MeasurementVolume)) {
      throw Error("Interaction this.measurementVolume is not defined");
    }

    if (this.measurementVolume && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      // 핸들러 상태 변경
      this.isOn = true;
      this.drawingPositions = [];

      // Ceisium Viewer
      const viewer = this.viewer;

      // viewer.scene.globe.depthTestAgainstTerrain = true;

      // 핸들러 생성
      this.handler = new ScreenSpaceEventHandler(viewer.canvas);

      // 라벨, 마우스 guid
      const guid = createGuid();

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementVolume.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 ",
        "체적을 측정할 선을 그려주세요",
        true,
        true
      );

      // LABEL - 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.measurementVolume.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 체적을 측정할 선을",
        "그려주세요",
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 영역 지정 폴리라인 생성
      const polyline = Utils.createPolyline({
        viewer: viewer,
        id: `Measurement_Volume_Drawing_Polyline_${guid}`,
        positions: new CallbackProperty(() => {
          let arr;
          if (!this._isMobile) {
            let middle = this.mousePosition;
            if (this.drawingPositions.length > 0) {
              const left = this.drawingPositions[
                this.drawingPositions.length - 1
              ];
              const right = this.mousePosition;
              middle = Cartesian3.lerp(left, right, 9 / 10, new Cartesian3());
            }
            arr = [...this.drawingPositions, middle];
          } else {
            arr = [...this.drawingPositions];
          }
          if (arr.length > 1) {
            return arr;
          }
        }, false),
      });
      this.drawingPolylineEntity = polyline;

      // 영역 지정 폴리곤 생성
      const polygon = Utils.createPolygon({
        viewer: viewer,
        id: `Measurement_Volume_Drawing_Polygon_${guid}`,
        positions: new CallbackProperty(() => {
          // PolygonHierarchy 변환 과정에 높이가 0로 바뀜 => 원본 Cartesian3 복사본 넘겨주기
          const arr = [];
          for (const drawingPosition of this.drawingPositions) {
            arr.push(Cartesian3.clone(drawingPosition, new Cartesian3()));
          }

          if (!this._isMobile) {
            arr.push(Cartesian3.clone(this.mousePosition, new Cartesian3()));
          }
          if (arr.length > 2) {
            const positions = new PolygonHierarchy(arr);
            return positions;
          }
        }, false),
      });
      this.drawingPolygonEntity = polygon;

      // 핸들러 실행
      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();

      // 경고창 생성
      this.createWarningAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "체적 측정 시, ",
        content2: "세 개 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });
    }
  }

  /**
   * 체적 측정 핸들러 종료. (MeasurementVolume 초기화 필수.)</br>
   * End volume measurement handler. (MeasurementVolume initialization is required.)
   */
  off() {
    if (this.measurementVolume && this.isOn) {
      const viewer = this.viewer;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");
      // depthTestAgainstTerrain 값 true로 변경 후, primitive 추가 할 경우 z fighting 발생 됨
      // viewer.scene.globe.depthTestAgainstTerrain = this.depthTest;

      // 영역 지정 엔티티 제거
      for (const entity of this.drawingPointEntity) {
        viewer.entities.remove(entity);
      }
      viewer.entities.remove(this.drawingPolylineEntity);
      viewer.entities.remove(this.drawingPolygonEntity);

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // 핸들러 제거
      if (!this.handler?.isDestroyed()) {
        this.handler?.destroy();
      }
      window.removeEventListener("keydown", this.keyDownEvent);

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      // 변수 초기화 -> 변수에 값을 넣어야 초기화가 되죠
      this.isOn = false;
      this.drawingPositions = [];
      this.drawingPointEntity = [];
      this.drawingPolylineEntity = undefined;
      this.drawingPolygonEntity = undefined;
      this.keyDownEvent = undefined;
      // alert창 삭제
      this.createWarningAlert.destroy();
      this.createWarningAlert = undefined;
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
      // this.measurementVolume = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
      // this.changeLengthLabelPosition();
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.off();
    this.destroyInstance();
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /*
   * 영역 포인트 추가. </br>
   * Add volume position.
   * @param {Cartesian3} position 포인트 좌표. </br> Cartesian to add.
   */
  addPosition(position) {
    const viewer = this.viewer;
    let isEqual = false;

    // 마지막으로 추가된 좌표와 현재 좌표 비교
    const drawingPositionsLength = this.drawingPositions.length;
    if (drawingPositionsLength > 0) {
      const lastPosition = this.drawingPositions[drawingPositionsLength - 1];
      if (lastPosition.equals(position)) {
        isEqual = true;
      }
    }

    // 영역 지정 포인트 생성
    if (!isEqual && defined(position)) {
      const guid = createGuid();

      const drawingPoint = Utils.createPoint({
        viewer: viewer,
        position: position,
        id: `Measurement_Volume_Drawing_Point_${guid}`,
      });
      this.drawingPointEntity.push(drawingPoint);

      // 영역 지정중인 좌표 추가
      this.drawingPositions.push(position);

      // 점 생성 시 콜백함수 실행
      if (this.addCallback && this.measurementVolume) {
        this.addCallback(this.measurementVolume);
      }

      if (
        this.drawingPositions.length === 1 &&
        this.startCallback &&
        this.measurementVolume
      ) {
        this.startCallback(this.measurementVolume);
      }
    }
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    const viewer = this.viewer;

    // // 영역 지정 마지막 포지션 제거
    //let lastPosition;
    // const drawingPositionsLength = this.drawingPositions.length;
    // if (drawingPositionsLength > 0) {
    //   lastPosition = this.drawingPositions[drawingPositionsLength - 1];
    //   this.drawingPositions = this.drawingPositions.slice(
    //     0,
    //     drawingPositionsLength - 1
    //   );
    // }

    // // 영역 지정 마지막 포인트 엔티티 제거
    // const drawingPointEntityLength = this.drawingPointEntity.length;
    // if (drawingPointEntityLength > 0) {
    //   const lastPointEntity = this.drawingPointEntity[
    //     drawingPointEntityLength - 1
    //   ];
    //   if (lastPointEntity.position._value.equals(lastPosition)) {
    //     viewer.entities.remove(lastPointEntity);
    //     this.drawingPointEntity = this.drawingPointEntity.slice(
    //       0,
    //       drawingPointEntityLength - 1
    //     );
    //   }
    // }

    if (this.polylinePrimitive) {
      viewer.scene.primitives.remove(this.polylinePrimitive);
    }

    // 영역 지정 마지막 좌표 제거
    const drawingPositionsLength = this.drawingPositions.length;
    if (drawingPositionsLength > 0) {
      // 배열 마지막 아이템 제거
      this.drawingPositions = this.drawingPositions.slice(
        0,
        drawingPositionsLength - 1
      );
    }
    const drawingPointEntityLength = this.drawingPointEntity.length;
    if (drawingPointEntityLength > 0) {
      const lastPointEntity = this.drawingPointEntity[
        drawingPointEntityLength - 1
      ];
      // 엔티티 제거
      viewer.entities.remove(lastPointEntity);
      // 배열 마지막 아이템 제거
      this.drawingPointEntity = this.drawingPointEntity.slice(
        0,
        drawingPointEntityLength - 1
      );
    }
  }

  /*
   * 입력된 좌표 값의 높이를 현재 지형의 높이로 변환. </br>
   * Change the height of the input position value to the Terrain height value of the current level.
   * @param {DitapViewer} viewer DitapViewer 인스턴스. </br> DitapViewer instance.
   * @param {Cartesian3} position 좌표. </br> Position.
   * @return {Cartesian3} 변환된 좌표. </br> Converted position.
   */
  convertToCurrentTerrainHeight(viewer, position) {
    if (!defined(position)) {
      return;
    }
    const ctg = Cartographic.fromCartesian(position);
    const height = viewer.scene.globe.getHeight(ctg);
    ctg.height = height;
    const newPosition = Cartographic.toCartesian(ctg);

    return newPosition;
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const allDynamicEntity = [
        ...this.drawingPointEntity,
        this.drawingPolygonEntity,
        this.drawingPolylineEntity,
      ];

      allDynamicEntity.forEach((entities) => {
        entities.show = false;
      });

      setTimeout(() => {
        // 좌표 추가
        if (this.isOn) {
          const position = Utils.pickPosition(e.position, this.viewer);
          if (defined(position)) {
            this.mousePosition = position;
            this.addPosition(position);
          }
        }
        allDynamicEntity.forEach((entities) => {
          entities.show = true;
        });
      }, 5);
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;
    handler.setInputAction(() => {
      if (0 < this.drawingPositions.length) {
        // 마지막 좌표 제거
        this.removePoint();
      } else {
        this.off();
        this.destroyInstance();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 마우스 더블 클릭 이벤트 핸들러 생성.</br>
   * Create mouse double-click event handler.
   */
  createDoubleClickHandler() {
    const handler = this.handler;
    handler.setInputAction(() => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 면생성 종료.<br>
   * End straight line distance measurement.
   */
  endHandler() {
    if (!(this.measurementVolume instanceof MeasurementVolume)) {
      throw Error("InteractionVolume this.measurementVolume is not defined");
    }

    const viewer = this.viewer;

    if (2 < this.drawingPositions.length) {
      setTimeout(() => {
        // measurement calcVolume
        this.measurementVolume.calculateVolume({
          positions: this.drawingPositions,
          planHeight: this.measurementVolume.planHeight,
          cellSize: this.measurementVolume.cellSize,
          showCell: this.measurementVolume.showCell,
          fillingVolumeColor: this.measurementVolume.fillingVolumeColor,
          cuttingVolumeColor: this.measurementVolume.cuttingVolumeColor,
          classification: this.measurementVolume.classification,
          loading: this.measurementVolume.loading,
          callback: () => {
            if (this.popup) {
              const popup =
                viewer.ditapStorage.volumePopups[this.measurementVolume.id];
              if (popup) {
                popup.open();
              } else {
                const popupMeasurementVolume = new PopupMeasurementVolume(
                  this.measurementVolume,
                  viewer.ditapPaneManager
                );
                popupMeasurementVolume.open();
                viewer.ditapStorage.volumePopups[
                  this.measurementVolume.id
                ] = popupMeasurementVolume;
              }
            }
            this.measurementVolume = undefined;
          },
        });

        // 마지막 점 생성 시 콜백함수 실행
        if (this.endCallback && this.measurementVolume) {
          this.endCallback(this.measurementVolume);
        }

        // on 종료
        this.off();
      }, 6);
    } else {
      this.createWarningAlert.open();
    }
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.isOn) {
        const position = Utils.pickPosition(e.endPosition, viewer);
        if (defined(position)) {
          // const newPosition = this.convertToCurrentTerrainHeight(
          //   viewer,
          //   position
          // );
          // if (defined(newPosition)) {
          // }
          this.mousePosition = position;

          // 도움말 라벨 좌표 이동
          const entity_pos = SceneTransforms.wgs84ToWindowCoordinates(
            viewer.scene,
            this.mousePosition
          );

          this.infoLabelEntity.classList.remove("hide");
          this.infoLabelEntity.style.left = `${Number(entity_pos.x + 20)}px`;
          this.infoLabelEntity.style.top = `${Number(entity_pos.y + 20)}px`;
          this.infoLabelEntity.style.right = "unset";

          if (this.polylinePrimitive) {
            viewer.scene.primitives.remove(this.polylinePrimitive);
          }

          const positions = this.drawingPositions;

          if (positions.length > 0) {
            const array = [];
            array.push(positions[positions.length - 1]);
            array.push(position);
            const polyline = Utils.createPolylinePrimitive({
              viewer: viewer,
              positions: array,
            });
            this.polylinePrimitive = polyline;
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 윈도우 좌표 -> 데카르트3 좌표 변환. </br>
   * Convert window position to Cartesian3 position.
   * @param {Cartesian2} window 윈도우 좌표. </br> Window position.
   * @returns {Cartesian3} 데카르트3 좌표. </br> Cartesian3 position.
   */
  windowToCartesian(window) {
    const viewer = this.viewer;
    // const position = viewer.scene.pickPosition(window);

    const position = viewer.scene.pickPosition(window);

    const ctg = Cartographic.fromCartesian(position);
    const height = viewer.scene.globe.getHeight(ctg);
    ctg.height = height;

    let newPosition;
    try {
      newPosition = Cartographic.toCartesian(ctg, new Cartesian3());
    } catch {
      newPosition = viewer.camera.pickEllipsoid(
        window,
        viewer.scene.globe.ellipsoid
      );
    }

    return newPosition;
  }

  /**
   * 생성중인 인스턴스 파괴.<br>
   * Destroying the instance being created.
   */
  destroyInstance() {
    // linestringEntity 삭제
    this.measurementVolume?.destroy();

    this.measurementVolume = undefined;
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    // ESC : 체적 측정 실행 취소
    if (event.key === "Escape") {
      this.off();
      this.destroyInstance();

      // Backspace : 마지막 좌표 제거
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}

export default InteractionVolume;
